import { defineStore } from 'pinia';
import { computed, reactive, ref, toRef } from 'vue';

import { DashboardAPI, LibraryAPI } from '@/apis';
import { useDashboardsReportFilters } from '@/app/dashboards/composables/useDashboardsReportFilters';
import { createTSICustomDateRange } from '@/app/dashboards/utils/date.utils';
import {
  CHANNEL_TO_CHANNELS_CONVERSION_REPORT_TYPES,
  REPORTS,
} from '@/app/dashboards/utils/reports.enum';
import { BASE_BREAKDOWN_OPTIONS } from '@/models/dashboards/aggregate-type.enum';
import { CHANNELS, TSI_ENABLED_CHANNELS } from '@/models/dashboards/channels.enum';
import { TOTAL_TSI_METRIC } from '@/models/dashboards/metrics.constants';
import { useIdentityStore } from '@/stores/identity';
import {
  convertDateToUtcDateTimeByZoneISOString,
  convertDateToUtcDateTimeByZoneString,
  convertUtcDateTimeToLocalDateTimeByZone,
} from '@/utils';
import { formatDatePattern, formatDateRangeLabel } from '@/utils/formatters';

export const useDashboardReportsStore = defineStore('dashboardReports', () => {
  // external store modules
  const identityStore = useIdentityStore();
  const {
    dashboardsFilters,
    updateReportDate,
    updateContextDate,
    updateReportingPeriodMode,
    updateContextPeriodMode,
    updateGraphScale,
    updateContentTagIds,
    updateContentTagMatchType,
    updateMessageTagIds,
    updateMessageTagMatchType,
    updateBrandIds,
    updateBrandTagIds,
    updateCompetitorSourceAccountIds,
    updateCompetitorTagIds,
    updateCampaignIds,
    updateBoardIds,
  } = useDashboardsReportFilters();

  // states
  const reportDateRange = toRef(dashboardsFilters, 'reportDateRange');
  const contextDateRange = toRef(dashboardsFilters, 'contextDateRange');
  const reportingPeriodMode = toRef(dashboardsFilters, 'reportingPeriodMode');
  const contextPeriodMode = toRef(dashboardsFilters, 'contextPeriodMode');
  const graphScale = toRef(dashboardsFilters, 'graphScale');
  const contentTagIds = toRef(dashboardsFilters, 'contentTagIds');
  const contentTagMatchType = toRef(dashboardsFilters, 'contentTagMatchType');
  const messageTagIds = toRef(dashboardsFilters, 'messageTagIds');
  const messageTagMatchType = toRef(dashboardsFilters, 'messageTagMatchType');
  const brandIds = toRef(dashboardsFilters, 'brandIds');
  const brandTagIds = toRef(dashboardsFilters, 'brandTagIds');
  const competitorSourceAccountIds = toRef(dashboardsFilters, 'competitorSourceAccountIds');
  const competitorTagIds = toRef(dashboardsFilters, 'competitorTagIds');
  const campaignIds = toRef(dashboardsFilters, 'campaignIds');
  const boardIds = toRef(dashboardsFilters, 'boardIds');
  const reportFormShow = ref(false);
  const reportFormMode = ref(null);
  const reportFormTarget = ref(null);
  const widgetFormShow = ref(false);
  const widgetFormMode = ref(null);
  const widgetFormTarget = ref(null);
  const storyBoardPopupShow = ref(false);
  const comparableMetrics = ref({});
  const competitorTags = ref([]);
  const sideDrawerVisible = ref(false);

  const reportForm = reactive({
    metricType: null,
    type: null,
    meta: {
      aggregate: null,
      benchmarks: [],
      brand_ids: [],
      brand_tags: [],
      channels: [],
      competitor_source_account_ids: [],
      competitor_tags: {},
      community: {
        filters: {
          message_tag_ids: [],
          message_types: [],
          sentiment: [],
          assignee_ids: [],
        },
      },
      creator_ids: [],
      creator_tag_ids: [],
      content_tag_ids: null,
      mediaTypes: [],
      metrics: [],
      sort_order: null,
      aggregate_by: null,
      static_dates: null,
      listening: {},
      custom_name: null,
    },
  });

  const timeZone = computed(() => identityStore.identity?.time_zone_name ?? '');

  const identityBrands = computed(() => {
    const brands = identityStore.identity?.brands || {};
    return Object.keys(brands).map((key) => brands[key]);
  });

  const reportFormTargetMeta = computed(() => {
    const reportType = reportFormTarget.value?.type;
    if (
      CHANNEL_TO_CHANNELS_CONVERSION_REPORT_TYPES.includes(reportType) &&
      reportFormTarget.value?.meta?.channel
    ) {
      const cleanedReportFormTargetMeta = {
        ...reportFormTarget.value?.meta,
        channels: [reportFormTarget.value?.meta?.channel],
      };
      delete cleanedReportFormTargetMeta.channel;
      return cleanedReportFormTargetMeta;
    }
    return reportFormTarget.value?.meta;
  });

  const reportStartDate = computed(() => reportDateRange.value?.[0]);

  const reportEndDate = computed(() => reportDateRange.value?.[1]);

  const contextStartDate = computed(() => contextDateRange.value?.[0]);

  const contextEndDate = computed(() => contextDateRange.value?.[1]);

  const reportDateRangeInUserTimezone = computed(() =>
    reportDateRange.value.map((date) => convertDateToUtcDateTimeByZoneString(date, timeZone.value)),
  );

  const contextDateRangeInUserTimezone = computed(() =>
    contextDateRange.value.map((date) =>
      convertDateToUtcDateTimeByZoneString(date, timeZone.value),
    ),
  );

  const reportDateRangeISOStringInUserTimezone = computed(() =>
    reportDateRange.value.map((value) =>
      convertDateToUtcDateTimeByZoneISOString(value, timeZone.value),
    ),
  );

  const contextDateRangeISOStringInUserTimezone = computed(() =>
    contextDateRange.value.map((value) =>
      convertDateToUtcDateTimeByZoneISOString(value, timeZone.value),
    ),
  );

  const reportDateRangeDisplay = computed(() =>
    formatDateRangeLabel(reportStartDate.value, reportEndDate.value, {
      forceYearDisplay: true,
    }),
  );

  const contextDateRangeDisplay = computed(() => {
    const rangeLabel = formatDateRangeLabel(contextStartDate.value, contextEndDate.value, {
      forceYearDisplay: true,
    });
    return rangeLabel ? `Compared to ${rangeLabel}` : '';
  });

  function launchAddReportForm() {
    reportFormMode.value = 'ADD';
    reportFormShow.value = true;
    reportFormTarget.value = null;
  }

  function updateReportForm({ originalReport }) {
    const { id, type, meta } = originalReport;
    const metricType = meta.metrics?.[0]?.includes('CM_') ? 'customMetrics' : 'platformMetrics';

    reportForm.id = id;
    reportForm.type = type;
    reportForm.metricType = metricType;

    if (meta.channels?.[0] === CHANNELS.TSI_CHANNELS.value) {
      meta.channels = TSI_ENABLED_CHANNELS;
      if (type === 'GRAPH') {
        meta.static_dates = createTSICustomDateRange();
      }
    }

    if (meta.metrics?.[0] === TOTAL_TSI_METRIC && type === REPORTS.STACKED_BAR_METRIC.value) {
      meta.aggregate_by = BASE_BREAKDOWN_OPTIONS.BRAND.value;
      meta.secondary_aggregate_by = BASE_BREAKDOWN_OPTIONS.CHANNEL.value;
    }
    reportForm.meta = {
      ...meta,
      reportType: type,
    };
  }

  function launchEditReportForm(reportToEdit) {
    reportFormMode.value = 'EDIT';
    reportFormShow.value = true;
    reportFormTarget.value = reportToEdit;
  }

  function launchDuplicateReportForm(reportToDuplicate) {
    reportFormMode.value = 'DUPLICATE';
    reportFormShow.value = true;
    reportFormTarget.value = reportToDuplicate;
  }

  function closeReportForm() {
    reportFormMode.value = null;
    reportFormShow.value = false;
    reportFormTarget.value = null;
  }

  function launchAddWidgetForm(reportToAdd) {
    widgetFormMode.value = 'ADD';
    widgetFormShow.value = true;
    widgetFormTarget.value = reportToAdd;
  }

  function launchEditWidgetForm(reportToEdit) {
    widgetFormMode.value = 'EDIT';
    widgetFormShow.value = true;
    widgetFormTarget.value = reportToEdit;
  }

  function closeWidgetForm() {
    widgetFormMode.value = null;
    widgetFormShow.value = false;
    widgetFormTarget.value = null;
  }

  function launchStoryBoardPopup() {
    storyBoardPopupShow.value = true;
  }

  function closeStoryBoardPopup() {
    storyBoardPopupShow.value = false;
  }

  async function fetchComparableMetrics() {
    const response = await DashboardAPI.getComparableMetrics();
    comparableMetrics.value = response?.data;
  }

  function getComparableMetricBasedOnChannel(channel, metric) {
    return comparableMetrics.value?.[metric]?.[channel];
  }

  async function fetchCompetitiveTags({ competitiveBrandIds }) {
    const response = await LibraryAPI.getCompetitorTagsByBrand({
      brandIds: competitiveBrandIds,
      includeCompetitors: true,
    });
    competitorTags.value = response?.data?.data;
  }

  function convertUtcDateTimeToLocalTimeByTimeZone(utcDateTime) {
    const UtcToLocalTimeByTimeZone = convertUtcDateTimeToLocalDateTimeByZone(
      utcDateTime,
      timeZone.value,
    );

    return formatDatePattern(UtcToLocalTimeByTimeZone, 'YYYY-MM-DD');
  }

  return {
    reportDateRange,
    contextDateRange,
    reportingPeriodMode,
    contextPeriodMode,
    graphScale,
    contentTagIds,
    contentTagMatchType,
    messageTagIds,
    messageTagMatchType,
    brandIds,
    brandTagIds,
    competitorSourceAccountIds,
    competitorTagIds,
    campaignIds,
    boardIds,
    reportStartDate,
    reportEndDate,
    contextStartDate,
    contextEndDate,
    reportDateRangeInUserTimezone,
    contextDateRangeInUserTimezone,
    reportDateRangeISOStringInUserTimezone,
    contextDateRangeISOStringInUserTimezone,
    reportDateRangeDisplay,
    contextDateRangeDisplay,
    reportForm,
    reportFormShow,
    reportFormMode,
    reportFormTarget,
    widgetFormShow,
    widgetFormMode,
    widgetFormTarget,
    storyBoardPopupShow,
    comparableMetrics,
    competitorTags,
    reportFormTargetMeta,
    identityBrands,
    sideDrawerVisible,
    launchAddReportForm,
    launchEditReportForm,
    launchDuplicateReportForm,
    closeReportForm,
    launchAddWidgetForm,
    launchEditWidgetForm,
    closeWidgetForm,
    launchStoryBoardPopup,
    closeStoryBoardPopup,
    fetchComparableMetrics,
    fetchCompetitiveTags,
    getComparableMetricBasedOnChannel,
    convertUtcDateTimeToLocalTimeByTimeZone,
    updateReportDate,
    updateContextDate,
    updateReportingPeriodMode,
    updateContextPeriodMode,
    updateGraphScale,
    updateContentTagIds,
    updateContentTagMatchType,
    updateMessageTagIds,
    updateMessageTagMatchType,
    updateBrandIds,
    updateBrandTagIds,
    updateCompetitorSourceAccountIds,
    updateCompetitorTagIds,
    updateCampaignIds,
    updateBoardIds,
    updateReportForm,
  };
});
